<template>
  <textarea
    ref="$textAreaRef"
    v-imask="mask"
    :rows
    :class="$style.element"
    :value="model"
    @input="onInputDebounced"
    @change="onChange"
    resize="false"
  />
</template>

<script lang="ts" setup>
import { useTemplateRef, computed, onMounted } from 'vue'
import type { FactoryArg } from 'imask'
import { IMaskDirective as vImask } from 'vue-imask'
import debounceFunc from 'lodash/debounce'

const props = withDefaults(
  defineProps<{
    autofocus?: boolean
    debounce?: number
    lazy?: boolean
    mask?: FactoryArg
    rows: number
  }>(),
  {
    autofocus: false,
    debounce: undefined,
    lazy: false,
    mask: undefined,
    rows: 1
  }
)

const model = defineModel<string>()

const $textAreaRef = useTemplateRef<HTMLTextAreaElement>('$textAreaRef')

const focus = () => {
  $textAreaRef.value?.focus()
}

const blur = () => {
  $textAreaRef.value?.blur()
}

const onInput = (evt: Event) => {
  if (!props.lazy) {
    model.value = (evt.target as HTMLTextAreaElement).value
  }
}

const onChange = (evt: Event) => {
  if (props.lazy) {
    model.value = (evt.target as HTMLTextAreaElement).value
  }
}

const onInputDebounced = computed(() => debounceFunc(onInput, props.debounce))

onMounted(() => {
  if (props.autofocus) {
    focus()
  }
})

defineExpose({ focus, blur })
</script>

<style lang="scss" module>
.element {
  outline: none;
  border: none;
  background: none;
  resize: none;

  // see https://stackoverflow.com/questions/2781549/removing-input-background-colour-for-chrome-autocomplete
  &:-webkit-autofill {
    transition-property: color, background;
    transition-delay: 9999s;
  }

  &:read-only {
    text-overflow: ellipsis;
  }

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
  }
}
</style>
